export const sidebar = {
  namespaced: true,
  state: () => {
    return {
      /**
       * The sidebar is visible.
       * If `false`, sidebar is not rendered at all
       */
      visible: true,
      /**
       * The sidebar is in `mini` mode. When in `mini` mode
       * Only icons are rendered on the sidebar
       */
      miniMode: false,
      /**
       * User has explicitly expanded the sidebar from `mini` mode.
       * Sometimes, the sidebar can be toggled into `normal` mode temporarily
       * when the user clicks on the icons in `mini` mode in order to access
       * dropdown menus. If `false`, the sidebar will be returned to `mini` mode
       * after the user clicks a menu item on from the dropdown menu.
       */
      pinned: true,
    };
  },
  mutations: {
    setAppSidebarState: (state, payload) => {
      state.visible = payload.visible;
      state.miniMode = payload.miniMode;
      state.pinned = payload.pinned;
    },
    /**
     * Show be used for mobile views to make the entire sidebar
     * visible or invisible
     */
    showSidebar: (state) => {
      state.visible = !state.visible;
      if (state.visible) {
        state.miniMode = false;
      } else {
        state.miniMode = true;
      }
    },
  },
  getters: {
    getSideBarNavItems: (_state, _getters, rootState, rootGetters) => {
      const inDevelopment = rootState.inDevelopment;
      const churchId = rootGetters['auth/inStoreResolvedOrgId']();
      const isGlobalUser = !!rootGetters['auth/isGlobalUser'];
      const isImpersonationActive =
        !!rootGetters['auth/GET_IMPERSONATION_SESSION'].active && isGlobalUser;
      const userId = rootGetters['auth/getAuthUserID']();

      return [
        {
          name: '$t__general.dashboard',
          route:
            isGlobalUser && !isImpersonationActive
              ? { name: 'GlobalDashboard' }
              : { name: 'ChurchDashboard', params: { churchId } },
          icon: 'bar_chart',
        },

        {
          title: true,
          name: '$t__general.church_administration',
          class: '',
          permission: isImpersonationActive || !isGlobalUser,
          wrapper: {
            element: '',
            attributes: {},
          },
        },

        {
          name: '$t__general.members',
          icon: 'las la-users',
          permission:
            (isImpersonationActive && rootGetters['permissions/canListMembersProfiles']) ||
            (rootGetters['permissions/canListMembersProfiles'] && !isGlobalUser),
          class: 'shared',
          children: [
            {
              name: '$t__general.all_members',
              route: { name: 'all_profiles', params: { churchId } },
              icon: 'las la-address-card',
              permission:
                (isImpersonationActive && rootGetters['permissions/canListMembersProfiles']) ||
                (rootGetters['permissions/canListMembersProfiles'] && !isGlobalUser),
            },
            {
              name: '$t__general.register_new_member',
              route: { name: 'new_members_profile', params: { churchId } },
              icon: 'las la-user-plus',
              permission:
                (isImpersonationActive && rootGetters['permissions/canEditMembersProfiles']) ||
                (rootGetters['permissions/canEditMembersProfiles'] && !isGlobalUser),
            },
            {
              name: '$t__general.todays_birthdays',
              route: { name: 'all_birthdays', params: { churchId }, query: { period: 'daily' } },
              icon: 'las la-birthday-cake',
              permission:
                (isImpersonationActive && rootGetters['permissions/canListMembersProfiles']) ||
                (rootGetters['permissions/canListMembersProfiles'] && !isGlobalUser),
            },
            {
              name: '$t__general.membership_follow_ups',
              route: {
                name: 'all_membership_follow_ups',
                params: { churchId },
                query: { status: 'ACTIVE' },
              },
              icon: 'las la-comment',
              permission:
                (isImpersonationActive && rootGetters['permissions/canListFollowUps']) ||
                (rootGetters['permissions/canListFollowUps'] && !isGlobalUser),
            },
            {
              name: '$t__general.self_registration_links',
              route: { name: 'self_registration_links', params: { churchId } },
              icon: 'las la-link',
              permission:
                (isImpersonationActive && rootGetters['permissions/canEditMembersProfiles']) ||
                (rootGetters['permissions/canEditMembersProfiles'] && !isGlobalUser),
            },
            {
              name: '$t__general.membership_requests',
              route: { name: 'membership_requests', params: { churchId, requestType: 'join' } },
              icon: 'las la-user-check',
              permission:
                (isImpersonationActive && rootGetters['permissions/canEditMembersProfiles']) ||
                (rootGetters['permissions/canEditMembersProfiles'] && !isGlobalUser),
            },
            {
              name: '$t__general.contact_details_access_requests',
              route: { name: 'contact_details_access_requests' },
              icon: 'las la-key',
              // Global users do not have this permission at all
              permission: rootGetters['permissions/canApproveContactDetailsAccesses'],
            },
            {
              name: '$t__general.internal_account_requests',
              route: { name: 'internal_account_requests', params: { churchId } },
              icon: 'las la-users-cog',
              permission: rootGetters['permissions/canViewMembersAccounts'],
            },
          ],
        },
        {
          name: '$t__general.services',
          icon: 'las la-church',
          permission:
            (isImpersonationActive && rootGetters['permissions/canViewServices']) ||
            (rootGetters['permissions/canViewServices'] && !isGlobalUser),
          children: [
            {
              name: '$t__general.all_services',
              route: { name: 'all_services', params: { churchId } },
              icon: 'las la-church',
              permission: rootGetters['permissions/canViewServices'],
            },
            {
              name: '$t__general.new_worship_service',
              route: { name: 'mass_create_service', params: { churchId } },
              icon: 'las la-plus-square',
              permission: rootGetters['permissions/canCreateServices'],
            },
            {
              name: '$t__general.rosters',
              route: { name: 'rosters', params: { churchId } },
              icon: 'las la-plus-square',
              permission: rootGetters['permissions/canViewServices'],
            },
            {
              name: '$t__general.service_types',
              route: { name: 'all_service_types', params: { churchId } },
              icon: 'las la-plus-square',
              permission: rootGetters['permissions/canListServiceTypes'],
            },
            {
              name: '$t__general.service_activities',
              route: { name: 'all_service_activities', params: { churchId } },
              icon: 'las la-file-alt',
              permission: rootGetters['permissions/canListServiceActivitiesTemplates'],
            },
            {
              name: '$t__general.service_activities_templates',
              route: { name: 'all_service_activities_templates', params: { churchId } },
              icon: 'las la-file-alt',
              permission: rootGetters['permissions/canListServiceActivitiesTemplates'],
            },
          ],
        },
        {
          name: '$t__general.visitors',
          icon: 'las la-user-friends',
          permission:
            (isImpersonationActive && rootGetters['permissions/canListVisitors']) ||
            (rootGetters['permissions/canListVisitors'] && !isGlobalUser),
          children: [
            {
              name: '$t__general.visitors_module.all_visitors',
              route: { name: 'all_visitors', params: { churchId } },
              icon: 'las la-user-friends',
              permission: rootGetters['permissions/canViewVisitors'],
            },
          ],
        },
        {
          name: '$t__general.fellowship_zones',
          icon: 'las la-share-alt',
          permission:
            (isImpersonationActive && rootGetters['permissions/canListZones']) ||
            (rootGetters['permissions/canListZones'] && !isGlobalUser),
          children: [
            {
              name: '$t__general.zones_module.all_zones',
              route: { name: 'all_zones', params: { churchId } },
              icon: 'las la-share-alt',
              permission: rootGetters['permissions/canViewZones'],
            },
            {
              name: '$t__general.create_new_zone',
              route: { name: 'add_zone', params: { churchId } },
              icon: 'las la-plus-square',
              permission: rootGetters['permissions/canEditZones'],
            },
          ],
        },
        {
          name: '$t__general.ministries',
          icon: 'las la-people-carry',
          permission:
            (isImpersonationActive && rootGetters['permissions/canListMinistries']) ||
            (rootGetters['permissions/canListMinistries'] && !isGlobalUser),
          children: [
            {
              name: '$t__general.ministries_module.all_ministries',
              route: { name: 'all_ministries', params: { churchId } },
              icon: 'las la-people-carry',
              permission: rootGetters['permissions/canViewMinistries'],
            },
            {
              name: '$t__general.create_new_ministry',
              route: { name: 'add_ministry', params: { churchId } },
              icon: 'las la-plus-square',
              permission: rootGetters['permissions/canEditMinistries'],
            },
          ],
        },
        {
          name: '$t__general.groups',
          icon: 'las la-object-group',
          permission:
            (isImpersonationActive && rootGetters['permissions/canListGroups']) ||
            (rootGetters['permissions/canListGroups'] && !isGlobalUser),
          children: [
            {
              name: `$t__general.groups_module.all_groups`,
              route: { name: 'all_groups', params: { churchId } },
              icon: 'las la-object-group',
              permission: rootGetters['permissions/canViewGroups'],
            },
            {
              name: `$t__general.create_new_group`,
              route: { name: 'add_group', params: { churchId } },
              icon: 'las la-plus-square',
              permission: rootGetters['permissions/canEditGroups'],
            },
          ],
        },
        {
          name: '$t__general.finances',
          icon: 'las la-coins',
          permission:
            (isImpersonationActive &&
              (rootGetters['permissions/canListFinancialBudgets'] ||
                rootGetters['permissions/canListFinancialRequests'])) ||
            ((rootGetters['permissions/canListFinancialRequests'] ||
              rootGetters['permissions/canListFinancialBudgets']) &&
              !isGlobalUser),
          children: [
            {
              name: '$t__general.financial_budgets',
              route: { name: 'all_financial_budgets', params: { churchId } },
              icon: 'las la-file-invoice-dollar',
              permission:
                (isImpersonationActive && rootGetters['permissions/canListFinancialBudgets']) ||
                (rootGetters['permissions/canListFinancialBudgets'] && !isGlobalUser),
            },
            {
              name: '$t__general.financial_requests',
              route: { name: 'all_financial_requests', params: { churchId } },
              icon: 'las la-file-invoice-dollar',
              permission:
                (isImpersonationActive && rootGetters['permissions/canListFinancialRequests']) ||
                (rootGetters['permissions/canListFinancialRequests'] && !isGlobalUser),
            },
            {
              name: '$t__router_file.payment_dashboard',
              route: { name: 'payment_dashboard', params: { churchId } },
              icon: 'las la-receipt',
              permission: inDevelopment,
              // permission:
              //   (isImpersonationActive && rootGetters['permissions/canListPayments']) ||
              //   (rootGetters['permissions/canListPayments'] && !isGlobalUser),
            },
          ],
        },
        {
          name: '$t__general.positions',
          icon: 'las la-sitemap',
          permission:
            (isImpersonationActive && rootGetters['permissions/canListPositions']) ||
            (rootGetters['permissions/canListPositions'] && !isGlobalUser),
          children: [
            {
              name: '$t__general.positions_module.all_positions',
              route: { name: 'all_positions', params: { churchId } },
              icon: 'las la-sitemap',
              permission:
                (isImpersonationActive && rootGetters['permissions/canViewPositions']) ||
                (rootGetters['permissions/canViewPositions'] && !isGlobalUser),
            },
            {
              name: '$t__general.create_new_position',
              route: { name: 'add_position', params: { churchId } },
              icon: 'las la-plus-square',
              permission:
                (isImpersonationActive && rootGetters['permissions/canCreatePositions']) ||
                (rootGetters['permissions/canCreatePositions'] && !isGlobalUser),
            },
          ],
        },
        {
          name: '$t__general.fundraisings',
          iconComponent: 'HandHoldingHeartIcon',
          permission:
            (isImpersonationActive && rootGetters['permissions/canListFundraisings']) ||
            (rootGetters['permissions/canListFundraisings'] && !isGlobalUser),
          children: [
            {
              name: '$t__general.fundraising_module.all_fundraisings',
              route: { name: 'all_fundraisings', params: { churchId } },
              iconComponent: 'HandHoldingHeartIcon',
              permission:
                (isImpersonationActive && rootGetters['permissions/canViewFundraisings']) ||
                (rootGetters['permissions/canViewFundraisings'] && !isGlobalUser),
            },
          ],
        },
        {
          name: '$t__general.reports',
          icon: 'las la-file-alt',

          permission:
            (isImpersonationActive &&
              rootGetters['permissions/canDownloadOrPrintReports'] &&
              rootGetters['permissions/canViewMembersProfiles']) ||
            (rootGetters['permissions/canDownloadOrPrintReports'] &&
              rootGetters['permissions/canViewMembersProfiles'] &&
              !isGlobalUser),
          children: [
            {
              name: '$t__general.membership_reports',
              route: { name: 'all_membership_reports', params: { churchId } },
              icon: 'las la-file-alt',
            },
          ],
        },

        {
          title: true,
          name: '$t__general.communications',
          class: '',
          wrapper: {
            element: '',
            attributes: {},
          },
        },

        {
          name: '$t__general.sms_center',
          icon: 'las la-sms',
          permission:
            (isImpersonationActive && rootGetters['permissions/canViewSmsSettings']) ||
            (rootGetters['permissions/canViewSmsSettings'] && !isGlobalUser),
          children: [
            {
              name: '$t__general.sms_dashboard',
              route: { name: 'sms_dashboard', params: { churchId } },
              icon: 'las la-tachometer-alt',
              permission: rootGetters['permissions/canViewSmsSettings'],
            },
            {
              name: '$t__general.sms_transactions',
              route: { name: 'all_sms_transactions', params: { churchId } },
              icon: 'las la-list',
              permission: rootGetters['permissions/canViewSmsSettings'],
            },
          ],
        },

        {
          name: '$t__general.mail_center',
          icon: 'las la-sms',
          permission: isGlobalUser,
          children: [
            /* {
              name: 'Mail Dashboard',
              route: { name: 'global_mail_dashboard', params: { userId } },
              icon: 'las la-tachometer-alt',
            }, */
            {
              name: '$t__general.new_mail',
              route: { name: 'global_compose_mail', params: { userId } },
              icon: 'las la-envelope',
            },
          ],
        },

        {
          title: true,
          name: '$t__general.global_administration',
          permission: isGlobalUser,
          class: '',
          wrapper: {
            element: '',
            attributes: {},
          },
        },

        {
          name: '$t__general.subscription_module.product_subscriptions',
          icon: 'las la-check-square',
          permission: isGlobalUser,
          children: [
            {
              name: '$t__general.subscription_module.product_subscriptions',
              route: 'product_subscriptions',
              icon: 'las la-sync',
              permission: rootGetters['permissions/canListProductSubscriptions'],
            },
            {
              name: '$t__general.subscription_module.product_subscription_refunds',
              route: 'product_subscription_refunds',
              icon: 'las la-wallet',
              permission: rootGetters['permissions/canListProductSubscriptionRefunds'],
            },
            {
              name: '$t__general.subscription_module.product_subscription_cancellations',
              route: 'product_subscription_cancellations',
              icon: 'las la-ban',
              permission: rootGetters['permissions/canListProductSubscriptions'],
            },
            {
              name: '$t__general.subscription_module.subscription_enquiries',
              route: 'subscription_enquiries',
              icon: 'las la-question-circle',
              permission: rootGetters['permissions/canListProductSubscriptionEnquiries'],
            },
          ],
        },

        {
          name: '$t__general.church_functions_module.church_functions',
          route: 'church_functions',
          icon: 'las la-bible',
          permission: rootGetters['permissions/canEditChurchFunctions'],
        },

        {
          name: '$t__general.churches',
          icon: 'las la-church',
          permission: rootGetters['permissions/canListOrganisations'] & isGlobalUser,
          children: [
            {
              name: '$t__general.all_churches',
              route: { name: 'all_organisations' },
              icon: 'las la-church',
              permission: rootGetters['permissions/canListOrganisations'] & isGlobalUser,
            },
            {
              name: '$t__general.new_church',
              route: { name: 'add_organisation' },
              icon: 'las la-plus-square',
              permission: rootGetters['permissions/canCreateOrganisations'] & isGlobalUser,
            },
            {
              name: '$t__general.deleted_churches',
              route: { name: 'deleted_organisations' },
              icon: 'las la-church',
              permission: rootGetters['permissions/canDeleteOrganisations'] & isGlobalUser,
            },
            {
              name: '$t__general.church_claims_module.church_claims',
              route: { name: 'church_claims' },
              icon: 'las la-church',
              permission: rootGetters['permissions/canEditOrganisations'] & isGlobalUser,
            },
          ],
        },
        {
          name: '$t__general.users',
          icon: 'las la-users',
          permission:
            (rootGetters['permissions/canListUsers'] ||
              rootGetters['permissions/canListGlobalUsers']) & isGlobalUser,
          children: [
            /* {
              name: `New Global User`,
              route: { name: 'new_members_account' },
              icon: 'las la-user-plus',
              permission: rootGetters['permissions/canCreateUsers'],
            },
            */
            {
              name: '$t__general.global_users_module.global_users',
              route: { name: 'global_users' },
              icon: 'las la-users',
              permission: rootGetters['permissions/canViewGlobalUsers'] & isGlobalUser,
            },
            {
              name: '$t__general.non_global_users',
              route: { name: 'non_global_users' },
              icon: 'las la-user-plus',
              permission: rootGetters['permissions/canListUsers'] & isGlobalUser,
            },
            {
              name: '$t__general.internal_account_requests',
              route: 'global_internal_account_requests',
              icon: 'las la-users-cog',
              permission: rootGetters['permissions/canEditUsers'] & isGlobalUser,
            },
          ],
        },
        {
          permission: rootGetters['permissions/canListSmsAccounts'] & isGlobalUser,
          name: '$t__general.sms_accounts',
          route: { name: 'sms_accounts', params: { userId } },
          icon: 'las la-comment-dollar',
        },

        {
          title: true,
          name: '$t__general.branch_management',
          class: '',
          wrapper: {
            element: '',
            attributes: {},
          },
          // Limit to global users for now
          permission: rootGetters['permissions/canListOrganisationGroups'] && isGlobalUser,
        },
        {
          name: '$t__general.all_denominations',
          route: 'all_denominations',
          icon: 'las la-church',
          permission: rootGetters['permissions/canListDenominations'],
        },
        {
          name: '$t__general.branch_management_dashboard',
          route: 'branch_management_dashboard',
          icon: 'las la-church',
          // Limit to global users for now
          permission: rootGetters['permissions/canListOrganisationGroups'] && isGlobalUser,
        },
        {
          name: '$t__general.pending_requests',
          route: 'branch_management_requests',
          icon: 'las la-church',
          // Limit to global users for now
          permission: rootGetters['permissions/canListOrganisationGroups'] && isGlobalUser,
        },
        {
          name: '$t__general.settings',
          icon: 'las la-cog',
          permission: rootGetters['permissions/canEditSystemSettings'] && isGlobalUser,
          children: [
            {
              name: '$t__general.global_settings',
              url: '/settings/global-settings',
              icon: 'las la-cog',
              permission: rootGetters['permissions/canEditSystemSettings'],
            },
          ],
        },

        {
          title: true,
          name: '$t__general.content_management',
          class: '',
          wrapper: {
            element: '',
            attributes: {},
          },
          permission: isGlobalUser && rootGetters['permissions/can_list_docs_articles'],
        },
        {
          name: '$t__general.product_documentations',
          route: { name: 'product_documentations' },
          iconComponent: 'HandPhoneIcon',
          permission: isGlobalUser && rootGetters['permissions/can_list_docs_articles'],
        },
        {
          name: '$t__general.legal_documents',
          route: { name: 'legal_documents' },
          iconComponent: 'LegalIcon',
          permission: rootGetters['permissions/canListLegalDocuments'],
        },
        {
          title: true,
          name: '$t__general.socials',
          class: '',
          wrapper: {
            element: '',
            attributes: {},
          },
        },
        {
          name: '$t__general.church_followers',
          route: { name: 'church_followers', params: { churchId } },
          icon: 'las la-hashtag',
          permission: !isGlobalUser && rootGetters['permissions/canListMembersProfiles'],
        },
        {
          name: '$t__general.church_timeline',
          route: { name: 'church_timeline', params: { churchId } },
          icon: 'las la-paper-plane',
          permission: !isGlobalUser && rootGetters['permissions/canListPosts'],
        },

        {
          title: true,
          name: '$t__general.moderation_reports',
          class: '',
          wrapper: {
            element: '',
            attributes: {},
          },
          permission: isGlobalUser,
        },
        {
          name: '$t__general.moderation_reports',
          route: { name: 'moderation_reports' },
          icon: 'las la-file-alt',
          permission: isGlobalUser,
          // && rootGetters['permissions/canListMembersProfiles'],
        },

        {
          title: true,
          name: '$t__general.other_tools',
          class: '',
          wrapper: {
            element: '',
            attributes: {},
          },
        },

        {
          name: '$t__general.church_administrators',
          icon: 'las la-user-shield',
          permission: rootGetters['permissions/canListMembersAccounts'] && !isGlobalUser,
          children: [
            {
              name: '$t__general.church_administrators',
              route: { name: 'church_admins', params: { churchId } },
              icon: 'las la-user-shield',
              permission: rootGetters['permissions/canListMembersAccounts'],
            },
            {
              name: '$t__general.pending_invitations',
              route: { name: 'church_admins_invitations', params: { churchId } },
              icon: 'las la-user-shield',
              permission: rootGetters['permissions/canListMembersAccounts'],
            },
          ],
        },

        {
          name: '$t__general.church_settings',
          icon: 'las la-cog',
          permission:
            (isImpersonationActive && rootGetters['permissions/canEditOrganisationSettings']) ||
            (rootGetters['permissions/canEditOrganisationSettings'] && !isGlobalUser),
          children: [
            {
              name: '$t__general.church_login_records',
              route: { name: 'church_login_records', params: { churchId } },
              icon: 'las la-file-alt',
              permission: rootGetters['permissions/canViewLogs'],
            },
            {
              name: '$t__general.my_church',
              route: { name: 'my_organisation', params: { churchId, tab: 'church-details' } },
              icon: 'las la-church',
            },
            {
              name: '$t__general.church_settings',
              route: { name: 'church_details_settings', params: { churchId } },
              icon: 'las la-users-cog',
            },
            {
              name: '$t__general.church_templates',
              route: { name: 'organisation_templates', params: { churchId } },
              icon: 'las la-sitemap',
            },
            {
              name: '$t__general.onboarding_data_migration',
              route: { name: 'onboarding_data_migration', params: { churchId } },
              icon: 'las la-arrow-circle-right',
              permission: rootGetters['permissions/canPerformChurchOnboarding'],
            },
          ],
        },

        {
          name: '$t__general.roles',
          url: '/roles',
          icon: 'las la-user-tag',

          permission: isGlobalUser && rootGetters['permissions/canViewRoles'],
          children: [
            {
              name: '$t__general.all_roles',
              url: '/roles',
              icon: 'las la-user-tag',
              permission: isGlobalUser && rootGetters['permissions/canViewRoles'],
            },
            {
              name: '$t__general.create_new_role',
              url: '/roles/new',
              icon: 'las la-plus-square',
              permission: isGlobalUser && rootGetters['permissions/canEditRoles'],
            },
          ],
        },
        {
          name: '$t__general.files',
          url: '/files',
          icon: 'las la-file-alt',
          permission: rootGetters['permissions/canListDownloadableFiles'],
          children: [
            {
              name: '$t__general.downloadable_files',
              url: '/files/downloadable-files',
              icon: 'las la-file-download',
            },
          ],
        },
        {
          name: '$t__general.notifications',
          route: { name: 'notifications' },
          icon: 'las la-bell',
          permission: rootGetters['permissions/canEditSelf'],
        },
        {
          name: '$t__general.my_settings',
          icon: 'las la-cog',
          permission: rootGetters['permissions/canEditSelf'],
          children: [
            {
              name: '$t__general.my_church_profile',
              icon: 'las la-address-card',
              route: { name: 'my_church_profile', params: { churchId } },
              permission: rootGetters['permissions/canEditSelf'] && !isGlobalUser,
            },
            {
              name: '$t__general.personal_settings',
              route: { name: 'view_account_settings', params: { tab: 'personal-profile' } },
              icon: 'las la-user-cog',
            },
          ],
        },
      ];
    },
    appSidebarState: (state) => ({
      visible: state.visible,
      miniMode: state.miniMode,
      pinned: state.pinned,
    }),
  },
};
